import { BotIcon } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import type { TaskAgentOwnerPresentation, TaskExecutionState } from "./taskExecutionPresentation";

export function ProjectTaskAccountableAgent(props: {
  readonly owner: TaskAgentOwnerPresentation;
  readonly headingId: string;
}) {
  const initials = props.owner.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  return (
    <section aria-labelledby={props.headingId} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={props.headingId} className="text-sm font-semibold text-foreground">
          Accountable agent
        </h3>
        <ExecutionStateBadge state={props.owner.state} />
      </div>
      <div
        className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3"
        title={`Stable agent identity: ${props.owner.stableKey}`}
      >
        <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
          {initials}
          <span className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full border-2 border-card bg-primary text-primary-foreground">
            <BotIcon className="size-2.5" aria-hidden />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">
              {props.owner.displayName}
            </span>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
              AI agent
            </Badge>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {humanize(props.owner.primaryRole)} · Agent #{props.owner.seatId}
            {props.owner.runId === null ? "" : ` · Run #${props.owner.runId}`}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {humanize(props.owner.stage)} · Stable project teammate
          </p>
        </div>
      </div>
    </section>
  );
}

const EXECUTION_STATE_LABEL: Record<TaskExecutionState, string> = {
  queued: "Queued",
  "working-now": "Working now",
  waiting: "Waiting",
  blocked: "Blocked",
  finished: "Finished",
};

function ExecutionStateBadge(props: { readonly state: TaskExecutionState }) {
  const tone =
    props.state === "working-now"
      ? "border-hue-sky-border bg-hue-sky-surface text-hue-sky-foreground"
      : props.state === "blocked"
        ? "border-destructive-border bg-destructive-surface text-destructive"
        : props.state === "finished"
          ? "border-success-border bg-success-surface text-success-foreground"
          : "border-border bg-muted text-muted-foreground";
  return (
    <Badge variant="outline" className={tone}>
      {EXECUTION_STATE_LABEL[props.state]}
    </Badge>
  );
}

function humanize(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
